import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { euro, num, dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";

const oggi = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const destinazione = (p) => (p.destinazione === "interno" ? "Uso interno" : p.commessa_codice);

// Reso a magazzino: si parte da un prelievo esistente e si indicano le quantità da restituire
export function ResoDialog({ open, onClose, prelievi, onSaved }) {
  const [prelievoId, setPrelievoId] = useState("");
  const [data, setData] = useState(oggi());
  const [note, setNote] = useState("");
  const [qta, setQta] = useState({});
  const restituibili = prelievi.filter((p) => !p.rettifica && p.righe.some((r) => (r.quantita_netta ?? r.quantita) > 1e-9));
  const sel = restituibili.find((p) => p.id === prelievoId);
  const chiave = (r) => `${r.articolo_id}||${r.variante || ""}`;
  const mut = useMutation({
    mutationFn: async () => (await api.post("/magazzino/prelievi/reso", {
      prelievo_id: prelievoId, data, note,
      righe: sel.righe.filter((r) => Number(qta[chiave(r)]) > 0).map((r) => ({ articolo_id: r.articolo_id, variante: r.variante || "", quantita: Number(qta[chiave(r)]) })),
    })).data,
    onSuccess: () => { toast.success("Reso registrato: merce tornata in giacenza"); onSaved(); onClose(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const submit = () => {
    if (!sel) return toast.error("Seleziona il prelievo da cui restituire");
    const righe = sel.righe.filter((r) => Number(qta[chiave(r)]) > 0);
    if (!righe.length) return toast.error("Indica almeno una quantità da restituire");
    const oltre = righe.find((r) => Number(qta[chiave(r)]) > (r.quantita_netta ?? r.quantita) + 1e-9);
    if (oltre) return toast.error(`${oltre.codice}: puoi restituire al massimo ${num(oltre.quantita_netta ?? oltre.quantita, 2)} ${oltre.um}`);
    mut.mutate();
  };
  const tuttoIlResto = () => sel && setQta(Object.fromEntries(sel.righe.map((r) => [chiave(r), r.quantita_netta ?? r.quantita])));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="reso-dialog">
        <DialogHeader><DialogTitle className="font-display">Nuovo reso a magazzino</DialogTitle><DialogDescription>Scegli il prelievo di origine e indica quanto rientra in giacenza. Il reso vale per qualsiasi destinazione (commessa, prestazione o uso interno).</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label>Prelievo di origine *</Label>
            <Select value={prelievoId} onValueChange={(v) => { setPrelievoId(v); setQta({}); }}>
              <SelectTrigger data-testid="reso-prelievo"><SelectValue placeholder="Seleziona il prelievo da cui restituire" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {restituibili.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`reso-prelievo-opzione-${p.id}`}>{dateIt(p.data)} · {p.dipendente_nome} · {destinazione(p)} · {p.righe.map((r) => `${r.codice} ${r.descrizione} · ${num(r.quantita_netta ?? r.quantita, 2)} ${r.um}`).join(" · ")} · {euro(p.totale)}</SelectItem>)}
                {restituibili.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Nessun prelievo con quantità restituibili.</div>}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Data reso</Label><Input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="reso-data" /></div>
          <div className="space-y-1.5"><Label>Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. materiale avanzato dal cantiere" data-testid="reso-note" /></div>
        </div>
        {sel && (
          <div className="mt-2 bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-3 py-2 flex items-center justify-between text-xs text-slate-500 border-b border-slate-100">
              <span>Articoli del prelievo · indica la quantità che rientra</span>
              <button type="button" onClick={tuttoIlResto} className="text-primary hover:underline font-medium" data-testid="reso-tutto">Restituisci tutto il residuo</button>
            </div>
            <table className="w-full text-sm" data-testid="reso-righe">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400"><th className="px-3 py-1.5">Articolo</th><th className="px-3 py-1.5 text-right">Prelevati</th><th className="px-3 py-1.5 text-right">Già resi</th><th className="px-3 py-1.5 text-right">Restituibili</th><th className="px-3 py-1.5 text-right w-32">Da restituire</th></tr></thead>
              <tbody>
                {sel.righe.map((r, i) => { const max = r.quantita_netta ?? r.quantita; return (
                  <tr key={i} className="border-t border-slate-50" data-testid={`reso-riga-${i}`}>
                    <td className="px-3 py-1.5"><span className="font-mono text-xs">{r.codice}</span> {r.descrizione}{r.variante ? ` · ${r.variante}` : ""}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{num(r.quantita, 2)} {r.um}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-slate-500">{r.quantita_resa ? num(r.quantita_resa, 2) : "—"}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold" data-testid={`reso-restituibile-${i}`}>{num(max, 2)}</td>
                    <td className="px-3 py-1.5"><Input type="number" min="0" max={max} step="any" disabled={max <= 1e-9} value={qta[chiave(r)] ?? ""} onChange={(e) => setQta((s) => ({ ...s, [chiave(r)]: e.target.value }))} className="h-8 px-2 text-right" data-testid={`reso-qta-${i}`} /></td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={mut.isPending || !sel} data-testid="reso-salva"><Undo2 className="h-4 w-4 mr-1" /> Registra reso</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
